import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { notify } from '../components/CustomToast';
import teamService from '../services/teamService';
import useTitle from '../hooks/useTitle';
import { useTeamContext } from '../context/TeamContext';
import { FaCheckCircle, FaExclamationTriangle, FaSpinner, FaEnvelope } from 'react-icons/fa';
import Logo from '../assets/logo.png';
import '../App.css';
import { useI18n } from '../i18n';

const AcceptInvite = () => {
  const { t } = useI18n();
  useTitle('pages.accept_invite.meta_title');

  const { token } = useParams();
  const navigate = useNavigate();
  const { addTeam } = useTeamContext();
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [inviteStatus, setInviteStatus] = useState(null);
  const [message, setMessage] = useState('');
  const [teamData, setTeamData] = useState(null);

  const hasAttemptedAccept = useRef(false);

  useEffect(() => {
    const checkAuthAndAccept = async () => {
      if (hasAttemptedAccept.current) return;

      const token_auth = localStorage.getItem('token');

      if (!token_auth) {
        setLoading(false);
        setInviteStatus('requires_login');
        setMessage(t('pages.accept_invite.requires_login_message'));
        sessionStorage.setItem('pending_invite_token', token);
        return;
      }

      hasAttemptedAccept.current = true;
      await handleAcceptInvite();
    };

    checkAuthAndAccept();
  }, [token, t]);

  const handleAcceptInvite = async () => {
    setAccepting(true);

    try {
      const response = await teamService.acceptInvite(token);

      if (response.status) {
        setInviteStatus('success');
        setMessage(response.message || t('messages.invite.joined_team'));
        setTeamData(response.data?.team);

        sessionStorage.removeItem('pending_invite_token');

        if (response.data?.team) {
          addTeam(response.data.team);
        }

        notify('success', t('messages.invite.accepted'));

        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        const fallbackMessage = t('errors.accept_invite_failed');
        setInviteStatus('error');
        setMessage(response.message || fallbackMessage);
        notify('error', response.message || fallbackMessage);
      }
    } catch (error) {
      const fallbackMessage = t('errors.invitation_invalid');
      setInviteStatus('error');
      setMessage(error.message || fallbackMessage);
      notify('error', error.message || fallbackMessage);
    } finally {
      setLoading(false);
      setAccepting(false);
    }
  };

  const handleLoginRedirect = () => {
    sessionStorage.setItem('pending_invite_token', token);
    navigate('/login');
  };

  return (
    <div className="login-container accept-invite-container">
      <div className="login-right accept-invite-card">
        <div className="login-form-container accept-invite-form">
          <div style={{ marginBottom: '30px' }}>
            <img src={Logo} alt={t('common.app_name')} style={{ height: '60px' }} />
          </div>

          {loading && (
            <div>
              <FaSpinner className="fa-spin" style={{ fontSize: '48px', color: '#1565D8', marginBottom: '20px' }} />
              <h2 style={{ color: '#1A1A1A', marginBottom: '10px' }}>{t('pages.accept_invite.processing_title')}</h2>
              <p style={{ color: '#718096' }}>{t('pages.accept_invite.processing_subtitle')}</p>
            </div>
          )}

          {!loading && inviteStatus === 'requires_login' && (
            <div>
              <FaEnvelope style={{ fontSize: '64px', color: '#1565D8', marginBottom: '20px' }} />
              <h2 className="ui-font-24" style={{ color: '#1A1A1A', marginBottom: '15px', fontWeight: '600' }}>
                {t('pages.accept_invite.requires_login_title')}
              </h2>
              <p className="ui-font-15" style={{ color: '#4A5568', marginBottom: '30px', lineHeight: '1.6' }}>{message}</p>

              <div
                style={{
                  background: '#EBF4FF',
                  padding: '20px',
                  borderRadius: '12px',
                  marginBottom: '30px',
                  border: '2px dashed #1565D8'
                }}
              >
                <p className="ui-font-14" style={{ color: '#1565D8', fontWeight: '500', margin: 0 }}>
                  {t('pages.accept_invite.requires_login_hint')}
                </p>
              </div>

              <button
                onClick={handleLoginRedirect}
                className="submit-btn"
                style={{
                  width: '100%',
                  marginBottom: '15px',
                  background: 'linear-gradient(135deg, #1565D8 0%, #0C4A9E 100%)'
                }}
                disabled={accepting}
              >
                {t('pages.accept_invite.login_to_accept')}
              </button>

              <p className="ui-font-14" style={{ color: '#718096', margin: '20px 0 0 0' }}>
                {t('pages.accept_invite.register_prompt')}{' '}
                <Link to="/register" style={{ color: '#1565D8', fontWeight: '500', textDecoration: 'none' }}>
                  {t('pages.accept_invite.register_here')}
                </Link>
              </p>
            </div>
          )}

          {!loading && inviteStatus === 'success' && (
            <div>
              <FaCheckCircle style={{ fontSize: '64px', color: '#48BB78', marginBottom: '20px' }} />
              <h2 className="ui-font-24" style={{ color: '#1A1A1A', marginBottom: '15px', fontWeight: '600' }}>
                {t('pages.accept_invite.success_title')}
              </h2>
              <p className="ui-font-15" style={{ color: '#4A5568', marginBottom: '20px', lineHeight: '1.6' }}>{message}</p>

              {teamData && (
                <div
                  style={{
                    background: '#F0FFF4',
                    padding: '20px',
                    borderRadius: '12px',
                    marginBottom: '30px',
                    border: '1px solid #9AE6B4'
                  }}
                >
                  <p style={{ color: '#22543D', fontWeight: '600', margin: '0 0 5px 0' }}>{t('pages.accept_invite.team_name')}</p>
                  <p className="ui-font-18" style={{ color: '#276749', fontWeight: '700', margin: 0 }}>{teamData.name}</p>
                </div>
              )}

              <p className="ui-font-14" style={{ color: '#718096' }}>{t('common.status.redirecting_dashboard')}</p>
            </div>
          )}

          {!loading && inviteStatus === 'error' && (
            <div>
              <FaExclamationTriangle style={{ fontSize: '64px', color: '#F56565', marginBottom: '20px' }} />
              <h2 className="ui-font-24" style={{ color: '#1A1A1A', marginBottom: '15px', fontWeight: '600' }}>
                {t('pages.accept_invite.error_title')}
              </h2>
              <p className="ui-font-15" style={{ color: '#4A5568', marginBottom: '30px', lineHeight: '1.6' }}>{message}</p>

              <div
                style={{
                  background: '#FFF5F5',
                  padding: '20px',
                  borderRadius: '12px',
                  marginBottom: '30px',
                  border: '1px solid #FC8181'
                }}
              >
                <p className="ui-font-14" style={{ color: '#742A2A', margin: 0, lineHeight: '1.6' }}>
                  {t('pages.accept_invite.error_description')}
                </p>
              </div>

              <Link to="/dashboard">
                <button
                  className="submit-btn"
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #1565D8 0%, #0C4A9E 100%)'
                  }}
                >
                  {t('pages.accept_invite.go_to_dashboard')}
                </button>
              </Link>

              <p className="ui-font-14" style={{ color: '#718096', marginTop: '20px' }}>
                <Link to="/dashboard" style={{ color: '#1565D8', fontWeight: '500', textDecoration: 'none' }}>
                  {t('pages.accept_invite.return_to_dashboard')}
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
